import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Send, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const AdminCommunication = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [announcements, setAnnouncements] = useState([]);
  const [form, setForm] = useState({ title: '', message: '', target: 'all', send_email: false });
  const [sending, setSending] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetch = async () => { try { const res = await axios.get(`${API}/admin/announcements`, { headers }); setAnnouncements(res.data); } catch {} };
  useEffect(() => { fetch(); }, []);

  const send = async () => {
    if (!form.title || !form.message) { toast({ title: 'Title and message required', variant: 'destructive' }); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/announcements`, form, { headers });
      toast({ title: 'Announcement sent', description: `Sent to ${res.data.recipient_count} users` });
      setForm({ title: '', message: '', target: 'all', send_email: false });
      fetch();
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail, variant: 'destructive' }); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-communication-page">
      <h1 className="text-2xl font-bold text-white mb-1">Communication</h1>
      <p className="text-gray-500 text-sm mb-6">Send announcements and notifications to users</p>

      <Card className="bg-[#132218] border-emerald-900/30 mb-6" data-testid="announcement-form">
        <CardContent className="p-5 space-y-4">
          <div><Label className="text-gray-400 text-xs">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="System update notification" className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="announcement-title" /></div>
          <div><Label className="text-gray-400 text-xs">Message</Label><Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={4} placeholder="Write your announcement..." className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="announcement-message" /></div>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label className="text-gray-400 text-xs">Target Audience</Label>
              <select value={form.target} onChange={e => setForm({...form, target: e.target.value})} className="w-full mt-1 h-9 rounded-md bg-black/20 border border-emerald-900/30 text-white text-sm px-3" data-testid="announcement-target">
                <option value="all">All Users</option>
                <option value="active">Active Users Only</option>
                <option value="plan:standard">Standard Plan Users</option>
                <option value="plan:growth">Growth Plan Users</option>
                <option value="plan:scale">Scale Plan Users</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer pb-1">
              <input type="checkbox" checked={form.send_email} onChange={e => setForm({...form, send_email: e.target.checked})} className="rounded border-emerald-900/30" data-testid="announcement-send-email" />
              <span className="text-sm text-gray-400">Also send via email</span>
            </label>
          </div>
          <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-announcement-btn"><Send className="w-4 h-4 mr-2" />{sending ? 'Sending...' : 'Send Announcement'}</Button>
        </CardContent>
      </Card>

      <h2 className="text-lg font-semibold text-white mb-3">Announcement History</h2>
      <div className="space-y-3">
        {announcements.map(ann => (
          <Card key={ann.id} className="bg-[#132218] border-emerald-900/30" data-testid={`announcement-${ann.id}`}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-white font-medium text-sm">{ann.title}</h3>
                  <p className="text-gray-400 text-xs mt-1 line-clamp-2">{ann.message}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <p className="text-emerald-400 text-xs font-medium">{ann.recipient_count} recipients</p>
                  <p className="text-gray-500 text-xs">{new Date(ann.created_at).toLocaleDateString()}</p>
                  {ann.send_email && <p className="text-blue-400 text-xs">Email sent</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {announcements.length === 0 && <p className="text-gray-500 text-center py-8">No announcements yet</p>}
      </div>
    </div>
  );
};

export default AdminCommunication;
